import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { img } from "../../lib/utils-app";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageUploader } from "../../components/ImageUploader";

const empty = { name: "", city: "", slug: "", image: "", description: { en: "", es: "" } };

export function NeighborhoodsManager() {
  const { lang } = useLang();
  const es = lang === "es";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => { setLoading(true); const { data } = await api.get("/neighborhoods"); setItems(data.items); setLoading(false); };
  useEffect(() => { load(); }, []);
  const remove = async (n) => { if (!window.confirm(es ? `¿Eliminar ${n.name}?` : `Delete ${n.name}?`)) return; await api.delete(`/neighborhoods/${n.id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl font-light">{es ? "Vecindarios" : "Neighborhoods"}</h1><p className="text-muted-foreground mt-1">{items.length}</p></div>
        <button onClick={() => { setEditing({ ...empty }); setOpen(true); }} className="flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110" data-testid="add-neighborhood-btn"><Plus size={16} /> {es ? "Nuevo" : "New"}</button>
      </div>
      {loading ? <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="neighborhoods-grid">
          {items.map((n) => (
            <div key={n.id} className="border border-border overflow-hidden" data-testid={`nb-card-${n.id}`}>
              <div className="aspect-[16/9] overflow-hidden"><img src={img(n.image)} alt="" className="w-full h-full object-cover" /></div>
              <div className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{n.name}</p><p className="text-xs text-muted-foreground truncate">{n.city}</p></div>
                <button onClick={() => { setEditing({ ...empty, ...n, description: n.description || { en: "", es: "" } }); setOpen(true); }} className="p-2 text-muted-foreground hover:text-primary" data-testid={`edit-nb-${n.id}`}><Pencil size={15} /></button>
                <button onClick={() => remove(n)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`delete-nb-${n.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <NbForm open={open} setOpen={setOpen} editing={editing} onSaved={load} lang={lang} />
    </div>
  );
}

function NbForm({ open, setOpen, editing, onSaved, lang }) {
  const es = lang === "es";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setD = (k, v) => setForm((f) => ({ ...f, description: { ...f.description, [k]: v } }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { if (form.id) await api.put(`/neighborhoods/id/${form.id}`, form); else await api.post("/neighborhoods", form); toast.success(es ? "Guardado" : "Saved"); setOpen(false); onSaved(); }
    catch { toast.error("Error"); } finally { setSaving(false); }
  };
  const inp = "w-full mt-1.5 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none";
  const lbl = "label-caps text-muted-foreground text-[0.6rem]";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{form.id ? (es ? "Editar" : "Edit") : (es ? "Nuevo" : "New")} {es ? "vecindario" : "neighborhood"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="neighborhood-form">
          <div><label className={lbl}>{es ? "Imagen" : "Image"}</label><div className="mt-1.5"><ImageUploader single aspect={16 / 9} value={form.image ? [form.image] : []} onChange={(a) => set("image", a[0] || "")} lang={lang} /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{es ? "Nombre" : "Name"}</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="nb-name" /></div>
            <div><label className={lbl}>{es ? "Ciudad" : "City"}</label><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} /></div>
          </div>
          <div><label className={lbl}>{es ? "Descripción (EN)" : "Description (EN)"}</label><textarea rows={2} value={form.description.en} onChange={(e) => setD("en", e.target.value)} className={inp + " resize-none"} /></div>
          <div><label className={lbl}>{es ? "Descripción (ES)" : "Description (ES)"}</label><textarea rows={2} value={form.description.es} onChange={(e) => setD("es", e.target.value)} className={inp + " resize-none"} /></div>
          <button disabled={saving} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="nb-save">{saving ? "…" : (es ? "Guardar" : "Save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
